import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Collapse,
  IconButton,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';
import {
  deleteGatewayExeJobs,
  fetchGatewayExeJobs,
  fetchGatewayExeTargets,
  redispatchGatewayExe,
  uploadGatewayExe,
  type GatewayExeJob,
  type GatewayExePool,
  type GatewayExeProduct,
} from '../../services/gatewayExeUpdateService';

function productLabel(product: string): string {
  return product === 'control_room' ? '관제실' : '현장';
}

function stateLabel(state: string): string {
  if (state === 'queued') return '수신 대기';
  if (state === 'staged') return '교체 시작';
  if (state === 'applied') return '적용 완료';
  if (state === 'failed') return '실패';
  if (state === 'stored') return '서버 보관';
  if (state === 'saved') return '저장 완료';
  return state || '—';
}

function detailLabel(detail?: string): string {
  const text = (detail || '').trim();
  if (!text || text === 'staged') return '';
  if (text === 'saved') {
    return '구버전 exe: Desktop\\data_gateway\\received 에 저장됨. DataGateway_new.exe 또는 본체 재배포 후 exe 폴더로 저장';
  }
  if (text.startsWith('saved ')) {
    const where = text.slice('saved '.length).trim();
    if (where.startsWith('next_to_exe:')) {
      const path = where.slice('next_to_exe:'.length);
      return `저장 위치: ${path}`;
    }
    if (where.startsWith('app_dir/') || where.includes('app_dir')) {
      return '저장 위치: DataGateway.exe와 같은 폴더(예: Desktop\\data_gateway)';
    }
    if (where.includes('received')) {
      return '저장 위치: …\\data_gateway\\received (구버전). exe 옆 폴더를 보려면 DataGateway.exe 재배포';
    }
    return `저장 위치: ${where}`;
  }
  if (text.includes('unsupported command')) {
    return '그 PC의 현재 exe가 배포 기능을 모름. DataGateway.exe를 그 PC에 한 번 직접 넣어 교체한 뒤 다시 시도';
  }
  if (text.includes('installed exe only')) return '파이썬 실행이 아니라 설치된 exe에서만 교체할 수 있음';
  if (text.toLowerCase().includes('sha256')) return '파일 검증 실패';
  if (text.startsWith('running ')) return '실행 중 exe 이름이 다름';
  return text;
}

/** staged만 오면 아직 재기동·페어링 전일 수 있음. applied까지 기다림. */
function isFullyDoneState(state: string): boolean {
  return state === 'saved' || state === 'applied' || state === 'failed';
}

function placeLabel(poolId: number | null | undefined, pools: GatewayExePool[]): string {
  if (poolId == null) return '관제실';
  const pool = pools.find((item) => item.id === poolId);
  const name = (pool?.name || pool?.code || '').trim();
  return name || `시설 ${poolId}`;
}

function resultText(job: GatewayExeJob, timedOut: boolean, pools: GatewayExePool[]): string {
  const name = job.original_name || job.staged_name || '파일';
  const targets = job.targets || [];
  if (targets.length === 0) return `${name} 대상 없음`;
  const parts = targets.map((target) => {
    const where = placeLabel(target.pool_id, pools);
    const extra = detailLabel(target.detail);
    return `${where} ${stateLabel(target.state)}${extra ? ` (${extra})` : ''}`;
  });
  const body = `${name}: ${parts.join(' / ')}`;
  if (timedOut && targets.some((target) => !isFullyDoneState(target.state))) {
    return `${body}. 교체 중 원격은 끊김이 정상. 1~2분 뒤 게이트웨이 온라인·적용 완료를 다시 확인`;
  }
  return body;
}

function resultSeverity(job: GatewayExeJob, timedOut: boolean): 'success' | 'error' | 'warning' | 'info' {
  const states = (job.targets || []).map((target) => target.state);
  if (states.some((state) => state === 'failed')) return 'error';
  if (timedOut && states.some((state) => !isFullyDoneState(state))) return 'warning';
  if (states.length > 0 && states.every((state) => state === 'saved' || state === 'applied')) return 'success';
  if (states.length > 0 && states.every((state) => state === 'saved' || state === 'staged' || state === 'applied')) {
    return 'info';
  }
  return 'info';
}

const GatewayExeUpdateCard: React.FC = () => {
  const [product, setProduct] = useState<GatewayExeProduct>('data_gateway');
  const [pools, setPools] = useState<GatewayExePool[]>([]);
  const poolsRef = useRef<GatewayExePool[]>([]);
  const [controlRoomOnline, setControlRoomOnline] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [jobs, setJobs] = useState<GatewayExeJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [infoSeverity, setInfoSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');
  const [watchId, setWatchId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [targets, items] = await Promise.all([
        fetchGatewayExeTargets(),
        fetchGatewayExeJobs(),
      ]);
      setPools(targets.pools || []);
      poolsRef.current = targets.pools || [];
      setControlRoomOnline(Boolean(targets.control_room_online));
      setJobs(items);
      setCheckedIds((cur) => cur.filter((id) => items.some((item) => item.id === id)));
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '배포 목록을 불러오지 못함'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!watchId) return undefined;
    let ticks = 0;
    let stopped = false;
    const timer = window.setInterval(() => {
      ticks += 1;
      void (async () => {
        try {
          const items = await fetchGatewayExeJobs();
          if (stopped) return;
          setJobs(items);
          const job = items.find((item) => item.id === watchId);
          if (!job) return;
          const targets = job.targets || [];
          const done =
            targets.length > 0 &&
            targets.every((target) => isFullyDoneState(target.state));
          // staged만 오면 재기동 대기. 약 2분(60*2s)까지 확인
          const timedOut = ticks >= 60 && !done;
          setInfo(resultText(job, timedOut, poolsRef.current));
          setInfoSeverity(resultSeverity(job, timedOut));
          if (done || timedOut) {
            stopped = true;
            window.clearInterval(timer);
            setWatchId(null);
            setInfo(resultText(job, timedOut, poolsRef.current));
            setInfoSeverity(resultSeverity(job, timedOut));
          }
        } catch {
          /* 다음 주기에 다시 확인 */
        }
      })();
    }, 2000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [watchId]);

  const togglePool = (id: number) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const onSend = async () => {
    if (!file) {
      setError('exe 파일을 선택');
      return;
    }
    if (product === 'data_gateway' && selected.length === 0) {
      setError('시설을 하나 이상 선택');
      return;
    }
    const isSwap =
      product === 'data_gateway'
        ? /^datagateway\.exe$/i.test(file.name)
        : /^controlroomgateway\.exe$/i.test(file.name);
    const isNewSafe =
      product === 'data_gateway' &&
      /^(datagateway_new|data_gateway_new|datagateway\.new)\.exe$/i.test(file.name);
    if (isSwap) {
      const ok = window.confirm(
        '파일 이름이 DataGateway.exe(또는 관제실 exe)라서 즉시 원격 교체·재시작. ' +
          '끊김 없이 보내려면 DataGateway_new.exe 로 이름을 바꿔 배포(1분 내 자동 교체·재시작, new 파일 삭제). 계속할까요?',
      );
      if (!ok) return;
    }
    setSending(true);
    setError(null);
    setInfo(null);
    try {
      const job = await uploadGatewayExe({
        file,
        product,
        poolIds: product === 'data_gateway' ? selected : [],
      });
      const sentTo =
        product === 'control_room'
          ? '관제실'
          : selected.map((id) => placeLabel(id, pools)).join(', ');
      const hint = isSwap
        ? '실행 파일 교체·재시작 중. 1~2분 뒤 온라인 확인'
        : isNewSafe
          ? 'DataGateway_new.exe 저장(실행 유지). 1분 내 중단·복사·재시작 후 new 파일 삭제'
          : '일반 파일 저장(재시작 없음). exe 폴더에 들어가는지 확인';
      setInfo(`${job.original_name || file.name} → ${sentTo}. ${hint}`);
      setInfoSeverity('info');
      setWatchId(job.id);
      setFile(null);
      setJobs((cur) => [job, ...cur.filter((item) => item.id !== job.id)]);
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '배포 실패'));
    } finally {
      setSending(false);
    }
  };

  const toggleJob = (id: string) => {
    setCheckedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const allChecked = jobs.length > 0 && checkedIds.length === jobs.length;
  const someChecked = checkedIds.length > 0 && checkedIds.length < jobs.length;

  const toggleAllJobs = () => {
    setCheckedIds(allChecked ? [] : jobs.map((job) => job.id));
  };

  const onDeleteChecked = async () => {
    if (checkedIds.length === 0) {
      setError('삭제할 항목을 선택');
      return;
    }
    if (!window.confirm(`선택한 배포 이력 ${checkedIds.length}건을 삭제할까요?`)) return;
    setDeleting(true);
    setError(null);
    try {
      const result = await deleteGatewayExeJobs(checkedIds);
      if (watchId && checkedIds.includes(watchId)) setWatchId(null);
      setCheckedIds([]);
      setInfo(`배포 이력 ${result.count}건 삭제`);
      setInfoSeverity('info');
      await load();
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '배포 이력 삭제 실패'));
    } finally {
      setDeleting(false);
    }
  };

  const onRetry = async (jobId: string) => {
    setSending(true);
    setError(null);
    try {
      const job = await redispatchGatewayExe(jobId);
      setInfo(`${job.original_name || '파일'} 다시 전달함. 게이트웨이 결과 확인 중`);
      setInfoSeverity('info');
      setWatchId(job.id);
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '다시 전달 실패'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Gateway.exe 배포"
        subheader="권장: DataGateway_new.exe 로 보내면 저장만 하고, 1분 내 자동 중단·복사·재시작 후 new 파일 삭제. DataGateway.exe 이름은 즉시 교체(끊김). 그 외 파일은 저장만"
        onClick={() => setOpen((value) => !value)}
        sx={{ cursor: 'pointer' }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={(ev) => ev.stopPropagation()}>
            <Button size="small" onClick={() => void load()} disabled={loading}>
              새로고침
            </Button>
            <IconButton size="small" aria-label={open ? '접기' : '펼치기'} onClick={() => setOpen((value) => !value)}>
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        }
      />
      <Collapse in={open}>
      <CardContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {info ? (
          <Alert severity={infoSeverity} sx={{ mb: 2 }} onClose={() => setInfo(null)}>
            {info}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="exe-product">대상</InputLabel>
            <Select
              labelId="exe-product"
              label="대상"
              value={product}
              onChange={(e) => setProduct(e.target.value as GatewayExeProduct)}
            >
              <MenuItem value="data_gateway">시설 DataGateway.exe</MenuItem>
              <MenuItem value="control_room">관제실 ControlRoomGateway.exe</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" component="label">
            파일 선택
            <input
              hidden
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Typography variant="body2">{file ? file.name : '파일 없음'}</Typography>
          <Button variant="contained" onClick={() => void onSend()} disabled={sending}>
            {sending ? '보내는 중…' : '배포'}
          </Button>
          {product === 'control_room' ? (
            <Chip
              size="small"
              color={controlRoomOnline ? 'success' : 'default'}
              label={controlRoomOnline ? '관제실 에이전트 온라인' : '관제실 에이전트 오프라인'}
            />
          ) : null}
        </Box>
        {product === 'data_gateway' ? (
          <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', mb: 2, p: 1 }}>
            {pools.length === 0 ? (
              <Typography variant="body2">시설 없음</Typography>
            ) : (
              pools.map((pool) => (
                <FormControlLabel
                  key={pool.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(pool.id)}
                      onChange={() => togglePool(pool.id)}
                    />
                  }
                  label={`${pool.name || pool.code} (${pool.id})`}
                />
              ))
            )}
          </Box>
        ) : null}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            disabled={deleting || checkedIds.length === 0}
            onClick={() => void onDeleteChecked()}
          >
            {deleting ? '삭제 중…' : `선택 삭제${checkedIds.length ? ` (${checkedIds.length})` : ''}`}
          </Button>
          <Typography variant="caption" color="text.secondary">
            이력·서버 보관 파일만 삭제. 시설 PC에 이미 간 파일은 그대로
          </Typography>
        </Box>
        {loading ? (
          <CircularProgress size={22} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={someChecked}
                    disabled={jobs.length === 0 || deleting}
                    onChange={toggleAllJobs}
                    inputProps={{ 'aria-label': '전체 선택' }}
                  />
                </TableCell>
                <TableCell>시각</TableCell>
                <TableCell>대상</TableCell>
                <TableCell>파일</TableCell>
                <TableCell>상태</TableCell>
                <TableCell align="right"> </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>배포 이력 없음</TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow key={job.id} selected={checkedIds.includes(job.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={checkedIds.includes(job.id)}
                        disabled={deleting}
                        onChange={() => toggleJob(job.id)}
                        inputProps={{ 'aria-label': '배포 이력 선택' }}
                      />
                    </TableCell>
                    <TableCell>{job.created_at || '—'}</TableCell>
                    <TableCell>
                      {(job.targets || []).length === 0 ? (
                        productLabel(job.product)
                      ) : (
                        (job.targets || []).map((target, idx) => (
                          <Typography key={`${job.id}-place-${idx}`} variant="body2" display="block">
                            {placeLabel(target.pool_id, pools)}
                          </Typography>
                        ))
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{job.original_name || '—'}</Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }} display="block">
                        {job.mode === 'save' ? '저장만' : '실행 파일 교체'}
                        {job.staged_name ? ` · ${job.staged_name}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(job.targets || []).map((t, idx) => (
                        <Box key={`${job.id}-${idx}`} sx={{ mb: 0.5 }}>
                          <Chip
                            size="small"
                            color={
                              t.state === 'failed'
                                ? 'error'
                                : t.state === 'saved' || t.state === 'staged'
                                  ? 'success'
                                  : t.state === 'queued'
                                    ? 'warning'
                                    : 'default'
                            }
                            label={`${placeLabel(t.pool_id, pools)} ${stateLabel(t.state)}`}
                          />
                          {detailLabel(t.detail) ? (
                            <Typography variant="caption" display="block">
                              {detailLabel(t.detail)}
                            </Typography>
                          ) : null}
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" disabled={sending} onClick={() => void onRetry(job.id)}>
                        다시 전달
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
      </Collapse>
    </Card>
  );
};

export default GatewayExeUpdateCard;
